import type { Edulead } from 'edulead';
import { useCallback, useEffect, useState } from 'react';
import { enumLabel } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import { getPusherClient } from '../services/pusher.service';
import { SearchHistoryItem } from '../services/search-history.service';

/**
 * A Go API search in the shape the panel renders, the shape the legacy Pusher items also have
 * (boss renders the panel's rows with it too). The Go API exposes no checksum; nothing reads it.
 */
function toItem(search: Edulead.EduleadV4Search | undefined): SearchHistoryItem {
  return {
    id: search?.id ?? '',
    name: search?.name ?? '',
    query: search?.query ?? '',
    checksum: '',
    entity: enumLabel(search?.entity, 'SEARCH_ENTITY_'),
    isStarred: search?.starred ?? false,
    isTeamShared: search?.shared ?? false,
    sharedByUserId: search?.sharer_id || null,
    userId: search?.owner_id ?? '',
    createdAt: search?.create_time ?? '',
    lastUsedAt: search?.last_used_time ?? '',
  };
}

interface UseSearchHistoryOptions {
  entity: 'CONTACT' | 'INSTITUTION';
  teamId: string;
  source?: string;
  userId: string;
  autoLoad?: boolean;
}

interface UseSearchHistoryResult {
  teamShared: SearchHistoryItem[];
  starred: SearchHistoryItem[];
  recent: SearchHistoryItem[];
  currentSearchId: string | undefined;
  isLoading: boolean;
  error: string | null;
  loadSearches: () => Promise<void>;
  toggleStar: (id: string) => Promise<void>;
  archiveSearch: (id: string) => Promise<void>;
  renameSearch: (id: string, name: string) => Promise<void>;
  shareWithTeam: (id: string) => Promise<void>;
  unshareFromTeam: (id: string) => Promise<void>;
}

export function useSearchHistory(options: UseSearchHistoryOptions): UseSearchHistoryResult {
  const { entity, teamId, source, userId, autoLoad = true } = options;

  const [teamShared, setTeamShared] = useState<SearchHistoryItem[]>([]);
  const [starred, setStarred] = useState<SearchHistoryItem[]>([]);
  const [recent, setRecent] = useState<SearchHistoryItem[]>([]);
  const [currentSearchId, setCurrentSearchId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSearches = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // The panel's three groups, newest first, 50 each (the API's default page). An unshared
      // search the caller sees is always their own. The team comes from the credential.
      const group = async (flags: { 'filter.starred'?: boolean; 'filter.shared': boolean }) => {
        const page = await eduleadV4.searches.list({
          'filter.entity': `SEARCH_ENTITY_${entity}`,
          'filter.source': source,
          ...flags,
        });
        return page.data.map(toItem);
      };
      const [shared, starredItems, recentItems] = await Promise.all([
        group({ 'filter.shared': true }),
        group({ 'filter.starred': true, 'filter.shared': false }),
        group({ 'filter.starred': false, 'filter.shared': false }),
      ]);
      setTeamShared(shared);
      setStarred(starredItems);
      setRecent(recentItems);
    } catch (err) {
      setError('Failed to load search history');
      console.error('Failed to load search history:', err);
    } finally {
      setIsLoading(false);
    }
  }, [entity, teamId, source]);

  const toggleStar = useCallback(
    async (id: string) => {
      try {
        const current = [...teamShared, ...starred, ...recent].find((s) => s.id === id);
        const { search } = await eduleadV4.searches.update({
          id,
          update_mask: 'starred',
          body: { starred: !current?.isStarred },
        });
        const updated = toItem(search);

        // Find which list the item is in and update it
        const updateItem = (items: SearchHistoryItem[]) =>
          items.map((s) => (s.id === id ? updated : s));

        // Move between starred/recent based on new state
        if (updated.isStarred) {
          // Move from recent to starred
          const item = recent.find((s) => s.id === id);
          if (item) {
            setRecent((prev) => prev.filter((s) => s.id !== id));
            setStarred((prev) => [updated, ...prev]);
          } else {
            // Item might be in teamShared - just update it there
            setTeamShared(updateItem);
          }
        } else {
          // Move from starred to recent
          const item = starred.find((s) => s.id === id);
          if (item) {
            setStarred((prev) => prev.filter((s) => s.id !== id));
            setRecent((prev) => [updated, ...prev]);
          } else {
            // Item might be in teamShared - just update it there
            setTeamShared(updateItem);
          }
        }
      } catch (err) {
        console.error('Failed to toggle star:', err);
        await loadSearches();
      }
    },
    [loadSearches, teamShared, recent, starred],
  );

  const archiveSearch = useCallback(
    async (id: string) => {
      try {
        await eduleadV4.searches.delete({ id });

        // Remove from all lists
        setTeamShared((prev) => prev.filter((s) => s.id !== id));
        setStarred((prev) => prev.filter((s) => s.id !== id));
        setRecent((prev) => prev.filter((s) => s.id !== id));
      } catch (err) {
        console.error('Failed to archive search:', err);
        await loadSearches();
      }
    },
    [loadSearches],
  );

  const renameSearch = useCallback(
    async (id: string, name: string) => {
      try {
        const { search } = await eduleadV4.searches.update({ id, update_mask: 'name', body: { name } });
        const updated = toItem(search);

        const updateItem = (items: SearchHistoryItem[]) =>
          items.map((s) => (s.id === id ? updated : s));

        setTeamShared(updateItem);
        setStarred(updateItem);
        setRecent(updateItem);
      } catch (err) {
        console.error('Failed to rename search:', err);
        await loadSearches();
      }
    },
    [loadSearches],
  );

  const shareWithTeam = useCallback(
    async (id: string) => {
      try {
        const { search } = await eduleadV4.searches.update({ id, update_mask: 'shared', body: { shared: true } });
        const updated = toItem(search);

        // Remove from starred/recent (item moves to teamShared only)
        setStarred((prev) => prev.filter((s) => s.id !== id));
        setRecent((prev) => prev.filter((s) => s.id !== id));

        // Add to teamShared if not already there
        if (!teamShared.find((s) => s.id === id)) {
          setTeamShared((prev) => [updated, ...prev]);
        }
      } catch (err) {
        console.error('Failed to share with team:', err);
        await loadSearches();
      }
    },
    [loadSearches, teamShared],
  );

  const unshareFromTeam = useCallback(
    async (id: string) => {
      try {
        const { search } = await eduleadV4.searches.update({ id, update_mask: 'shared', body: { shared: false } });
        const updated = toItem(search);

        // Remove from teamShared
        setTeamShared((prev) => prev.filter((s) => s.id !== id));

        // Add back to starred or recent based on isStarred
        if (updated.isStarred) {
          setStarred((prev) => [updated, ...prev]);
        } else {
          setRecent((prev) => [updated, ...prev]);
        }
      } catch (err) {
        console.error('Failed to unshare from team:', err);
        await loadSearches();
      }
    },
    [loadSearches],
  );

  // Auto-load on mount if enabled
  useEffect(() => {
    if (autoLoad) {
      loadSearches();
    }
  }, [autoLoad, loadSearches]);

  // Subscribe to Pusher for real-time updates
  useEffect(() => {
    if (!userId) return;

    const client = getPusherClient();
    const channelName = `search-history-${userId}`;
    const channel = client.subscribe(channelName);

    // New search created - add to top of recent list and track as current
    channel.bind('search:created', (data: { item: SearchHistoryItem }) => {
      const item = data.item;
      setRecent((prev) => [item, ...prev]);
      setCurrentSearchId(item.id);
    });

    // Existing search updated (e.g., timestamp bumped for duplicate search)
    // Update in place without reordering to avoid UI jumping, and track as current
    channel.bind('search:updated', (data: { item: SearchHistoryItem }) => {
      const item = data.item;

      const updateItem = (items: SearchHistoryItem[]) =>
        items.map((s) => (s.id === item.id ? item : s));

      setTeamShared(updateItem);
      setStarred(updateItem);
      setRecent(updateItem);
      setCurrentSearchId(item.id);
    });

    return () => {
      channel.unbind_all();
      client.unsubscribe(channelName);
    };
  }, [userId]);

  return {
    teamShared,
    starred,
    recent,
    currentSearchId,
    isLoading,
    error,
    loadSearches,
    toggleStar,
    archiveSearch,
    renameSearch,
    shareWithTeam,
    unshareFromTeam,
  };
}
